import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Swords, Clock, Trophy, ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import MatchupCard from "@/components/MatchupCard";

const MyDebates = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [debates, setDebates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchDebates();
  }, [user, navigate]);

  const fetchDebates = async () => {
    try {
      const res = await axios.get(`${API}/users/${user.user_id}/debates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebates(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const liveDebates = debates.filter(d => d.status === "live");
  const openDebates = debates.filter(d => d.status === "open");
  const completedDebates = debates.filter(d => d.status === "completed");

  const wins = completedDebates.filter(d => d.winner_id === user?.user_id).length;
  const losses = completedDebates.filter(d => d.winner_id && d.winner_id !== user?.user_id).length;

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative">
      {/* Ambient red/blue glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[20%] w-[70%] h-[50%] rounded-full bg-red-600/20 blur-[100px]" />
        <div className="absolute -bottom-[20%] -right-[20%] w-[70%] h-[50%] rounded-full bg-blue-600/25 blur-[100px]" />
      </div>
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link 
          to="/browse" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Debates
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">My Debates</h1>
            <p className="text-muted-foreground">Track your battles and victories</p>
          </div>
          <Link to="/create">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase btn-electric">
              <Plus className="w-4 h-4 mr-2" />
              New Debate
            </Button>
          </Link>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card/30 border border-white/10 rounded-lg p-4 text-center">
            <Swords className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold mono">{debates.length}</p>
            <p className="text-xs text-muted-foreground uppercase">Total</p>
          </div>
          <div className="bg-card/30 border border-white/10 rounded-lg p-4 text-center">
            <div className="w-6 h-6 mx-auto mb-2 flex items-center justify-center">
              <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse-live"></span>
            </div>
            <p className="text-2xl font-bold mono">{liveDebates.length}</p>
            <p className="text-xs text-muted-foreground uppercase">Live</p>
          </div>
          <div className="bg-card/30 border border-white/10 rounded-lg p-4 text-center">
            <Trophy className="w-6 h-6 mx-auto mb-2 text-accent" />
            <p className="text-2xl font-bold mono">{wins}</p>
            <p className="text-xs text-muted-foreground uppercase">Wins</p>
          </div>
          <div className="bg-card/30 border border-white/10 rounded-lg p-4 text-center">
            <Clock className="w-6 h-6 mx-auto mb-2 text-secondary" />
            <p className="text-2xl font-bold mono">{openDebates.length}</p>
            <p className="text-xs text-muted-foreground uppercase">Open</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : debates.length === 0 ? (
          <div className="text-center py-24 bg-card/30 rounded-lg border border-white/10">
            <Swords className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Debates Yet</h3>
            <p className="text-muted-foreground mb-6">Start your first debate and show the world what you've got!</p>
            <Link to="/create">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                Create Your First Debate
              </Button>
            </Link>
          </div>
        ) : (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="bg-card/30 border border-white/10 mb-8">
              <TabsTrigger value="all" data-testid="tab-all">All ({debates.length})</TabsTrigger>
              <TabsTrigger value="live" data-testid="tab-live">Live ({liveDebates.length})</TabsTrigger>
              <TabsTrigger value="open" data-testid="tab-open">Open ({openDebates.length})</TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">Completed ({completedDebates.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {debates.map(debate => (
                  <MatchupCard key={debate.debate_id} debate={debate} showUserBadge userId={user?.user_id} />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="live">
              {liveDebates.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No live debates</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {liveDebates.map(debate => (
                    <MatchupCard key={debate.debate_id} debate={debate} showUserBadge userId={user?.user_id} />
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="open">
              {openDebates.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No open debates</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {openDebates.map(debate => (
                    <MatchupCard key={debate.debate_id} debate={debate} showUserBadge userId={user?.user_id} />
                  ))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="completed">
              {completedDebates.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">No completed debates</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {completedDebates.map(debate => (
                    <MatchupCard key={debate.debate_id} debate={debate} showUserBadge userId={user?.user_id} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default MyDebates;
